import { createSlice, current } from '@reduxjs/toolkit'

import {
  changeHeader,
  setCSVFileData,
  resetCSVFileData,
  removeCSVDataRow,
  changeCSVData,
  setValidationInfo,
  setShowProgressBar,
  mappedFailedSubscribers,
  setActiveStep,
  setOptionsLanguages,
  setDialogEditCSVDataRow,
  setSendInvitation,
  setCancelImportDialog,
  setIsOpenDeleteCSVFileDialog,
  setIsOpenImportedSubscribersDialog,
  resetValidationInfo,
  setIsSocketConnected,
  setImportedSubscribersIds,
  setUploadingEnd,
  setUploadingProgress,
  setUploadingStart,
  setValidatingStart,
  setImportingStart,
  setImportingEnd,
  setInitilization,
  setValidationErrorMessage,
  setTags,
} from './reducers'
import {
  cancelImportThunk,
  importSubscribersThunk,
  sendCSVFileDataToValidateThunk,
  sendSingleRowToValidateThunk,
} from './importCSVExtraReducers'
import { STEP_PROGRESS_ONE } from 'app/constants/subscriberCRM'

export const initialState = {
  data: {
    headers: [],
    rows: [],
    fileName: '',
    fileSize: '',
    infoMappedHeaders: [],
    sendInvitation: false,
    importedSubscribersIds: [],
    importedSubscribersCount: 0,
    tags: [],
    validationInfo: {
      uploadingPercent: 0,
      percent: 0,
      type: null,
      chunkCount: 0,
      importId: null,
      validatedChunkCount: 0,
      failedSubscribers: [],
    },
  },
  isSocketConnected: false,
  cancelProcessDialog: null,
  isOpenDeleteCSVFileDialog: false,
  isOpenImportedSubscribersDialog: false,
  optionsLanguages: [],
  uploadingAxiosController: null,
  validatingAxiosController: null,
  importingAxiosController: null,
  showUploadingProgressBar: false,
  showProgressBar: false,
  activeStep: STEP_PROGRESS_ONE,
  dialogEditCSVData: {
    data: {},
    open: false,
    index: undefined,
  },
  validationErrorMessage: 'Something went wrong, please try again later',
}

const importCSVSlice = createSlice({
  name: 'importCSVData',
  initialState,
  reducers: {
    setCSVFileData,
    changeHeader,
    resetCSVFileData,
    removeCSVDataRow,
    changeCSVData,
    setValidationInfo,
    setShowProgressBar,
    mappedFailedSubscribers,
    setActiveStep,
    setOptionsLanguages,
    setDialogEditCSVDataRow,
    setSendInvitation,
    setCancelImportDialog,
    setIsOpenDeleteCSVFileDialog,
    setIsOpenImportedSubscribersDialog,
    resetValidationInfo,
    setIsSocketConnected,
    setImportedSubscribersIds,
    setUploadingEnd,
    setUploadingProgress,
    setUploadingStart,
    setValidatingStart,
    setImportingStart,
    setImportingEnd,
    setInitilization,
    setValidationErrorMessage,
    setTags,
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendCSVFileDataToValidateThunk.pending.type, (state, action) => {
        const { activeStep, data = {} } = action.meta.arg || {}
        if (activeStep) {
          state.activeStep = activeStep
        }
        state.isSocketConnected = true
        state.data = { ...state.data, ...data }
        state.showProgressBar = false
        state.showUploadingProgressBar = true
      })
      .addCase(sendSingleRowToValidateThunk.fulfilled.type, (state, action) => {
        const {
          data: { validationInfo },
        } = current(state)
        const {
          payload: { checkedRowsForDuplicates },
          meta,
        } = action
        const { index } = meta.arg
        const { failedSubscribers } = validationInfo
        const changedSubscriber = checkedRowsForDuplicates[index] || {}
        const updatedFailedSubscribers = failedSubscribers
          .map((subscriber) => {
            const matchingRow = checkedRowsForDuplicates.find(
              (row) => row.id === subscriber.id,
            )
            if (matchingRow && matchingRow.errors) {
              return {
                ...subscriber,
                ...matchingRow.errors.invalidFields,
              }
            } else {
              return null
            }
          })
          .filter(Boolean)
        if (changedSubscriber.errors) {
          const isAlreadyInFailedSubscribers = updatedFailedSubscribers.some(
            ({ id }) => id === changedSubscriber.id,
          )

          if (!isAlreadyInFailedSubscribers) {
            const { id, errors } = changedSubscriber
            updatedFailedSubscribers.push({ id, ...errors.invalidFields })
          }
        }
        state.data.rows = checkedRowsForDuplicates
        state.data.validationInfo = {
          ...validationInfo,
          failedSubscribers: updatedFailedSubscribers,
        }
      })
      .addCase(importSubscribersThunk.pending.type, (state) => {
        state.isSocketConnected = true
        state.showUploadingProgressBar = true
        state.showProgressBar = false
      })
      .addCase(cancelImportThunk.fulfilled.type, (state) => {
        return { ...initialState, isSocketConnected: false }
      })
  },
})

export default importCSVSlice
